import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";

interface ContentTypeConfig {
  type: string;
  label: string;
  area: string;
  enabled: boolean;
  icon: string;
}

export default function ContentTypesConfig() {
  const [configs, setConfigs] = useState<ContentTypeConfig[]>([]);
  const [userArea, setUserArea] = useState<string>('');

  useEffect(() => {
    loadUserProfile();
    loadConfigs();
  }, []);

  const loadUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('health_area')
      .eq('id', user.id)
      .single();

    if (profile) {
      setUserArea(profile.health_area || '');
    }
  };

  const loadConfigs = async () => {
    const defaultConfigs: ContentTypeConfig[] = [
      { type: 'results', label: 'Exames Laboratoriais', area: 'Geral', enabled: true, icon: '🧪' },
      { type: 'exam', label: 'Exames de Imagem', area: 'Geral', enabled: true, icon: '📸' },
      { type: 'prescription', label: 'Prescrições', area: 'Geral', enabled: true, icon: '💊' },
      { type: 'report', label: 'Laudos', area: 'Geral', enabled: true, icon: '📋' },
      { type: 'anamnesis', label: 'Anamnese', area: 'Geral', enabled: true, icon: '📝' },
      { type: 'physiotherapy_evolution', label: 'Evolução Fisioterápica', area: 'Fisioterapia', enabled: true, icon: '💪' },
      { type: 'nutritional_assessment', label: 'Avaliação Nutricional', area: 'Nutrição', enabled: true, icon: '🍎' },
      { type: 'meal_plan', label: 'Plano Alimentar', area: 'Nutrição', enabled: true, icon: '🥗' },
      { type: 'training_plan', label: 'Plano de Treino', area: 'Educação Física', enabled: true, icon: '🏋️' },
      { type: 'body_composition', label: 'Composição Corporal', area: 'Transversal', enabled: true, icon: '📊' },
      { type: 'psychotherapy_session', label: 'Sessão de Psicoterapia', area: 'Psicologia', enabled: true, icon: '🧠' },
      { type: 'chiropractic_adjustment', label: 'Ajuste Quiroprático', area: 'Quiropraxia', enabled: true, icon: '🦴' },
      { type: 'aesthetic_protocol', label: 'Protocolo Estético', area: 'Estética', enabled: true, icon: '✨' },
      { type: 'vitals', label: 'Sinais Vitais', area: 'Transversal', enabled: true, icon: '❤️' },
      { type: 'anthropometry', label: 'Antropometria', area: 'Transversal', enabled: true, icon: '📏' },
    ];
    
    setConfigs(defaultConfigs);
  };

  const toggleType = (type: string) => {
    setConfigs(prev => prev.map(c => 
      c.type === type ? { ...c, enabled: !c.enabled } : c
    ));
    toast.success('Configuração atualizada!');
  };

  return (
    <div className="space-y-6">
      {/* AdminBreadcrumbs é automático baseado na rota */}
      
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configuração de Tipos de Documentos</h1>
        <p className="text-muted-foreground">
          Habilite ou desabilite tipos de documentos relevantes para sua área de atuação
        </p>
        {userArea && (
          <Badge variant="secondary" className="mt-3">
            Área: {userArea}
          </Badge>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {configs.map(config => (
          <Card key={config.type}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{config.icon}</span>
                  <span className="text-base">{config.label}</span>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={() => toggleType(config.type)}
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Área:</Label>
                <Badge variant="outline">{config.area}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
